<template>
  <v-app id="app">
    <loading-screen v-if="!state.firstLoad"/>
    <v-content v-else>
      <v-progress-linear
        v-if="$route.name && !routeNames[$route.name].hidden_menu"
        :indeterminate="true"
        :active="state.loading"
        class="ma-0 position-absolute top-0"
        color="primary"
        height="4"
      ></v-progress-linear>
      <toolbar v-if="$route.name && !routeNames[$route.name].hidden_menu" />
      <v-container fluid justify-center fill-height class="pa-0" style="max-width: 100% !important;">
        <v-layout justify-center align-start>
          <v-fade-transition mode="out-in">
            <router-view :key="$route.fullPath"/>
          </v-fade-transition>
        </v-layout>
      </v-container>
    </v-content>
    <v-snackbar
      v-model="state.message.show"
      bottom
      :color="state.message.color"
      :timeout="state.message.timeout"
    >
      {{ state.message.text }}
      <v-spacer/>
      <v-btn
        text
        class="mx-0 white--text"
        v-if="state.message.redirect"
        @click="$router.push({ name: state.message.redirect.routeName })">
        {{ state.message.redirect.text }}
      </v-btn>
      <v-btn
        text
        class="mx-0 white--text"
        v-if="state.message.timeout === 0"
        @click="state.message.show = false">
        {{$t('actions.dismiss')}}
      </v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import { APP_SERVICE_ID } from '@/config/environment'
import { mapState } from 'vuex'
import routeNames from '@/router/routes'
import store from '@/store'
export default {
  name: 'App',
  data () {
    return {
      routeNames,
    }
  },
  components: {
    LoadingScreen: () => import('@/components/app-components/LoadingScreen.vue'),
    Toolbar: () => import('@/components/app-components/Toolbar.vue'),
  },
  computed: {
    ...mapState(['state', 'auth']),
    configuration () {
      return this.$store.state.service.configuration
    },
    settings () {
      return this.$store.state.me.settings
    },
  },
  watch: {
    configuration (newValue, oldValue) {
      if (newValue) {
        let theme = this.$vuetify.theme.themes.light
        if (newValue.customization.primaryColor !== undefined) {
          theme.primary = {
            base: newValue.customization.primaryColor.base,
            lighten1: newValue.customization.primaryColor.light1,
            lighten2: newValue.customization.primaryColor.light2,
            darken1: newValue.customization.primaryColor.dark1,
            darken2: newValue.customization.primaryColor.dark2,
          }
        }
        if (newValue.customization.secondaryColor !== undefined) {
          theme.secondary = {
            base: newValue.customization.secondaryColor.base,
            lighten1: newValue.customization.secondaryColor.light1,
            lighten2: newValue.customization.secondaryColor.light2,
            darken1: newValue.customization.secondaryColor.dark1,
            darken2: newValue.customization.secondaryColor.dark2,
          }
        }

        if (newValue.customization.gradient !== undefined) {
          theme.accent = {
            lighten1: newValue.customization.gradient.startColor,
            darken1: newValue.customization.gradient.endColor,
          }
        }
        this.$vuetify.theme.themes.light = theme
        var head = document.getElementsByTagName('head')[0]
        let js = document.createElement('meta')
        js.setAttribute('name', 'theme-color')
        js.setAttribute('content', newValue.customization.primaryColor.base)
        head.appendChild(js)

        if (newValue.customization.appTitle !== null) {
          let languageCode = this.$i18n.locale
          let appTitle = newValue.customization.appTitle.filter(
            (name) => name.languageCode === languageCode
          )[0]
          document.title = appTitle.value
          return appTitle.value
        }
      }
    },
    settings (newValue, oldValue) {
      if (newValue.languageCode !== undefined && newValue.languageCode !== null) {
        this.$i18n.locale = newValue.languageCode
        this.$vuetify.lang.current = newValue.languageCode
      }
    },
  },
  created () {
    if (store.state.state.inactivityTimeout !== null) {
      var time = store.state.state.inactivityTimeout

      setInterval(() => {
        if (store.state.auth.authToken !== null) {
          if (store.state.idleVue.isIdle) {
            time -= 1
          } else {
            time = store.state.state.inactivityTimeout
          }

          if (time < 1) {
            store.dispatch('auth/signOut')
          }
        }
      }, 1000)
    }
    this.$store.dispatch('service/getConfiguration').catch(() => {
      if (APP_SERVICE_ID) {
        this.$store.dispatch('service/getServiceConfiguration')
      }
    })
  },
}
</script>

<style lang="scss">

html, body {
  overflow: hidden !important;
  height: 100%;
  min-height: -webkit-stretch-content;
}
#app {
  width: 100% !important;
  position:fixed;
  top:0;
  bottom:0;
  overflow:hidden;
}

.v-content {
  max-height: 100%;
  .v-content__wrap {
    overflow: auto;
    position: relative;
  }
}
* {
  word-break: break-word;
}
.v-application .v-application--wrap {
  min-height: 100% !important;
}
.overflow-y-hidden {
  height: 100%;
}
.fit-content {
  min-height: fit-content !important;
}
.flex-none {
  flex: none !important;
}
.wrap-reverse {
  flex-wrap: wrap-reverse !important;
}
.hidden {
  visibility: hidden;
}
.margin-auto {
  margin: auto !important;
}
.full-height {
  height: 100%;
}
.login-background {
  background-size: cover;
  background-image: linear-gradient(to right top, var(--v-accent-lighten1), var(--v-accent-darken1));
  background-blend-mode: overlay;
}
.max-width-400 {
  padding: 40px 20px 40px 20px !important;
  max-width: 440px !important;
}
.full-width {
  width: 100%;
}
.pre-wrap {
  white-space: pre-wrap;
}
.theme--light.v-input--is-disabled input {
  color: rgba(0,0,0,.87);
}
.required label::after{
  content: '*';
}
.app-section-width {
  max-width: 600px !important;
}
.app-scroll-section {
  height: 100% !important;
  overflow-y: scroll !important;
  overflow-x: hidden;
}
.v-btn.primary-gradient {
  border: double 1px transparent;
  background: linear-gradient(white, white), linear-gradient(225deg, var(--v-primary-lighten1), var(--v-primary-darken1));
  background-origin: border-box;
  background-clip: content-box, border-box;
  padding: 0 !important;
}
.v-card.border-gradient:hover {
  border: double 2px transparent;
  background: linear-gradient(white, white), linear-gradient(225deg, var(--v-primary-lighten1), var(--v-primary-lighten1));
  background-origin: border-box;
  background-clip: content-box, border-box;
  padding: 0 !important;
}
.clickable {
  cursor: pointer;
}
.underline {
  text-decoration-line: underline;
}
.activity-title {
  white-space: nowrap;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  display: inline-flex;
  color: white;
  background: rgba(black, 0.5);
  padding: 2px 10px !important;
}
.activity-header {
  background-size: cover;
  background-position: center center;
  height: 135px !important;
}
.activity-button {
  border-radius: 4px;
  background-size: cover;
  background-position: center center;
  height: 135px !important;
}
.show-spaces {
  white-space: pre;
}
.line-through {
  text-decoration: line-through;
}
</style>
